/**
 * PassageProvenanceView — Highlighted draft with source attribution
 *
 * Renders draft text with color-coded passages showing which source card
 * each section came from. Supports:
 * - Verbatim quotes (solid left border)
 * - Paraphrased passages (subtle background)
 * - Click to navigate to source card
 * - Active source dimming (click source → only its passages glow)
 *
 * @module @humanizer/studio/components/workspace/PassageProvenanceView
 */

import { useMemo, useCallback, useRef, useState } from 'react';

interface PassageProvenance {
  type: 'verbatim' | 'paraphrase' | 'synthesis';
  sourceCardIndices: number[];
  startOffset: number;
  endOffset: number;
}

interface PassageProvenanceViewProps {
  /** The clean draft text (markers already stripped) */
  draftText: string;
  /** Provenance entries mapping text ranges to source cards */
  provenances: PassageProvenance[];
  /** Source card summaries for tooltip display */
  sourceCards: Array<{ title: string; excerpt: string }>;
  /** Callback when a passage is clicked */
  onSourceClick?: (cardIndex: number) => void;
  /** Active source card index (dims all other passages) */
  activeSourceIndex?: number | null;
  /** Whether provenance was adjusted after a transform */
  provenanceAdjusted?: boolean;
}

/** CSS custom property colors cycle every 8 cards */
const COLOR_VARS = [
  'var(--provenance-0)',
  'var(--provenance-1)',
  'var(--provenance-2)',
  'var(--provenance-3)',
  'var(--provenance-4)',
  'var(--provenance-5)',
  'var(--provenance-6)',
  'var(--provenance-7)',
];

function getColorVar(cardIndex: number): string {
  return COLOR_VARS[cardIndex % COLOR_VARS.length];
}

interface TextSegment {
  text: string;
  provenance?: PassageProvenance;
}

export function PassageProvenanceView({
  draftText,
  provenances,
  sourceCards,
  onSourceClick,
  activeSourceIndex,
  provenanceAdjusted,
}: PassageProvenanceViewProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);

  // Break draft text into segments: attributed and unattributed
  const segments = useMemo<TextSegment[]>(() => {
    if (!provenances || provenances.length === 0) {
      return [{ text: draftText }];
    }

    const result: TextSegment[] = [];
    let cursor = 0;

    // Sort by start offset (should already be sorted but ensure)
    const sorted = [...provenances].sort((a, b) => a.startOffset - b.startOffset);

    for (const p of sorted) {
      let start = Math.max(p.startOffset, cursor);
      let end = Math.min(p.endOffset, draftText.length);

      // Snap to word boundaries to avoid mid-word highlights
      while (start > cursor && start < draftText.length && !/\s/.test(draftText[start - 1] ?? '')) start--;
      while (end < draftText.length && !/\s/.test(draftText[end] ?? '')) end++;

      // Add unattributed text before this provenance
      if (start > cursor) {
        result.push({ text: draftText.slice(cursor, start) });
      }

      // Add attributed text
      if (end > start) {
        result.push({ text: draftText.slice(start, end), provenance: p });
      }

      cursor = end;
    }

    // Add remaining unattributed text
    if (cursor < draftText.length) {
      result.push({ text: draftText.slice(cursor) });
    }

    return result;
  }, [draftText, provenances]);

  const handleMarkClick = useCallback((p: PassageProvenance) => {
    if (onSourceClick && p.sourceCardIndices.length > 0) {
      onSourceClick(p.sourceCardIndices[0]);
    }
  }, [onSourceClick]);

  const handleMarkHover = useCallback((e: React.MouseEvent, p: PassageProvenance) => {
    const cardIdx = p.sourceCardIndices[0];
    if (cardIdx === undefined) return;
    setHoveredIndex(cardIdx);
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setTooltipPos({
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const handleMarkLeave = useCallback(() => {
    setHoveredIndex(null);
    setTooltipPos(null);
  }, []);

  return (
    <div className="passage-provenance">
      {provenanceAdjusted && (
        <div className="passage-provenance__warning">
          Source highlighting may be approximate — text was transformed after draft generation.
        </div>
      )}

      <div className="passage-provenance__text">
        {segments.map((seg, i) => {
          if (!seg.provenance) {
            // Unattributed text — render as-is with paragraph breaks
            return <span key={i}>{seg.text}</span>;
          }

          const p = seg.provenance;
          const primaryCard = p.sourceCardIndices[0];
          const colorVar = getColorVar(primaryCard);
          const className = `passage-mark--${p.type}${
            activeSourceIndex !== null && activeSourceIndex !== undefined && !p.sourceCardIndices.includes(activeSourceIndex)
              ? ' passage-mark--dimmed'
              : ''
          }`;

          return (
            <mark
              key={i}
              className={className}
              style={{ '--mark-color': colorVar } as React.CSSProperties}
              onClick={() => handleMarkClick(p)}
              onMouseEnter={(e) => handleMarkHover(e, p)}
              onMouseLeave={handleMarkLeave}
              title={sourceCards[primaryCard]?.title ?? `Card ${primaryCard + 1}`}
            >
              {seg.text}
            </mark>
          );
        })}
      </div>

      {hoveredIndex !== null && tooltipPos && (
        <div
          ref={tooltipRef}
          className="passage-mark__tooltip"
          style={{
            position: 'fixed',
            top: tooltipPos.top,
            left: tooltipPos.left,
          }}
        >
          Card {hoveredIndex + 1}: {sourceCards[hoveredIndex]?.title ?? 'Unknown'}
        </div>
      )}
    </div>
  );
}
